import React from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';
import Helmet from 'react-helmet-async';
import {get, isEmpty} from 'lodash';
import classNames from 'classnames';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {Link} from '@homeaway/react-localization-components';
import Experiment from '@homeaway/react-experiment';
import updateADL from '../../utils/adl';
import {isIE} from '../../utils/browserUtils';
import haWrapper from '../../utils/haWrapper';
import translate from '../../utils/translations';
import {LoadingError} from '../LoadingError/LoadingError';
import {ConversationQuery} from './ConversationQuery';
import {ConversationDetailsDetermineIsMobile} from './ConversationDetails/ConversationDetails';
import {ConversationMessages} from './ConversationMessages/ConversationMessages';
import ConversationNavbar from './ConversationNavbar/ConversationNavbar';
import {ConversationPayments} from './ConversationPayments/ConversationPayments';
import {tripHeaderImageSelected} from '../../utils/EDAP/Conversation';
import {MessagesProvider} from '../context/MessagesContext';
import {RefundInfoProvider} from '../context/RefundInfoContext';
import {StayProvider} from '../context/StayContext';
import {SystemProvider} from '../context/SystemContext';
import {createStayObject} from '../../utils/stayDomain';
import {createSystemDataObject} from '../../utils/systemData';
import type {Stay} from '../../utils/stayDomain';
import type {System} from '../../utils/systemData';
import {INTENTS} from '../molecules/VaFloating/VaFloating';
import {SKILL_TYPE} from '../molecules/VaFloating/constants';
import {TRIP_DETAILS} from '../../utils/edapUtils';
import {VaController} from './VaController/VaController';
import {
    EntryModalProvider,
    EntryModalContext,
} from '../context/EntryModalContext';
import {
    VaControllerContext,
    VaControllerProvider,
} from '../context/VaControllerContext';

import {
    getArrivalBotHelpText,
    onCloseArrivalBotTooltip,
} from '../VaWrap/utility';
import {getDaysFromCurrentDate} from '../../utils/dates';

import './Conversation.less';

export class Conversation extends React.Component {
    static propTypes = {
        activeTab: PropTypes.string,
        backUrl: PropTypes.string,
        categories: PropTypes.arrayOf(PropTypes.string),
        conversationId: PropTypes.string,
        isHybridInbox: PropTypes.bool,
        systemData: PropTypes.shape({}),
        tabChange: PropTypes.bool,
        translations: PropTypes.shape({}),
    };

    static defaultProps = {
        activeTab: 'details',
        backUrl: '/traveler/th/inbox',
        categories: [],
        systemData: {},
    };

    constructor(props, context) {
        super(props, context);
        this.messagesListRef = React.createRef();
        this.messageWindowRef = React.createRef();
        this.state = this.getStateFromProps(props);
    }

    componentDidMount() {
        // If SSR failed, retry
        const {conversation, getTravelerStay} = this.props.data || {};
        if (!conversation && !getTravelerStay?.booking) {
            this.props.data.refetch();
        } else {
            this.pageview();
            haWrapper.userTiming.mark('primary.action.rendered');
        }
    }

    componentDidUpdate(prevProps) {
        const {data} = this.props || {};
        const {activeTab, tabChange} = this.props;
        const tabs = this.getTabs();
        this.pageview();

        if (data !== prevProps.data && !data.loading) {
            this.setState({
                activeTab,
                activeTabIndex: tabs.indexOf(activeTab),
                tabs,
            });
            if (!tabChange) {
                haWrapper.userTiming.mark('primary.action.rendered');
            }
        }
    }

    getStateFromProps = (props) => {
        const tabs = this.getTabs();
        return {
            activeTab: props.activeTab,
            activeTabIndex: tabs.indexOf(props.activeTab),
            tabs,
            messages:
                props.data &&
                props.data.conversation &&
                props.data.conversation.messages,
            shouldSetGogInvitePopupValue: true,
        };
    };

    getTabs = () => {
        const {data, isHybridInbox} = this.props || {};
        let tabs = ['back'];

        if (!isHybridInbox) {
            tabs.push('details');
        }
        if (data.conversation?.messages) {
            tabs.push('messages');
        }
        if (!isHybridInbox && data.conversation?.priceDetails) {
            tabs.push('payments');
        }
        return tabs;
    };

    pageview = () => {
        window.analyticsdatalayer.reservationids = get(
            this.props,
            'data.getTravelerStay.booking.uuid',
            '-1'
        );
        updateADL(`travelerdash conversation ${this.props.activeTab}`);
    };

    fetch = () => {
        this.props.data.refetch();
    };

    handelBannerClick = (reservationUUID, endDate, startDate) => {
        const {activeTab} = this.state;
        tripHeaderImageSelected({
            actionLocation: activeTab,
            reservationUUID,
            endDate,
            startDate,
        });
    };

    updateVaContext = (checkInDate, phaseOfStay, vaContext) => {
        if (!this.openVa) {
            const daysToCheckin = getDaysFromCurrentDate(checkInDate);
            const eventsInfo = {
                actionLocation: TRIP_DETAILS,
                daysToCheckin: `${daysToCheckin}`,
                phaseOfStay,
                skillType: SKILL_TYPE.ARRIVAL,
            };
            vaContext.actions.updateVaController(
                false,
                eventsInfo,
                INTENTS.ARRIVAL_BOT
            );
            this.openVa = true;
        }
    };

    render() {
        const {backUrl, isHybridInbox, systemData, translations, search} =
            this.props;
        const travelerStayData = this.props.data?.getTravelerStay || {};
        const {booking, phaseOfStay, stayDisplayStatus, uiComponentDetail} =
            travelerStayData;
        const ctaDetails = travelerStayData.ctaDetails || {};
        const getStayDisplayStatus = stayDisplayStatus || {};
        const {
            arrivalBot = false,
            gogInvitePopup = false,
            refundStatusWidget = false,
        } = uiComponentDetail || {};
        const propertyAddress = travelerStayData.propertyAddress;
        const getTravelerStayBooking = booking || {};
        const globalMessages = getTravelerStayBooking?.globalMessages;
        // get() doesn't catch 'null'
        const conversation = get(this.props, 'data.conversation') || {};
        const listing =
            travelerStayData.booking?.listing || conversation.listing || {};
        const phoneNumbers =
            travelerStayData?.propertyContact?.phoneNumbers || {};
        const travelerDisplayablePhoneNumber =
            phoneNumbers.travelerDisplayablePhoneNumber || {};
        let {fullPhoneNumber, fullPhoneDigits, phoneNumber} =
            travelerDisplayablePhoneNumber;
        fullPhoneNumber = fullPhoneNumber || phoneNumber;
        fullPhoneDigits = fullPhoneDigits || phoneNumber;
        const contact = listing.contact || {};
        const countdownSummary = conversation.countdownSummary || {};
        const ownerSummary = conversation.ownerSummary || {};
        const quoteSummary = conversation.quoteSummary || {};
        const tripSummary = conversation.tripSummary || {};
        const vasSummary = conversation.valueAddedServicesSummary || {};
        const ownerAvatar = ownerSummary && ownerSummary.avatar;
        const propertyImageC5 = get(
            listing,
            'images[0].c5_uri',
            'http://csvcus.homeaway.com/rsrcs/cdn-logos/2.12.0/bce/brand/misc/property-image-placeholder.svg'
        );
        const city = propertyAddress?.city || listing?.address?.city || '';
        const location = get(listing, 'geography.description', city);
        const propertyImage = get(
            listing,
            'images[0].uri',
            'http://csvcus.homeaway.com/rsrcs/cdn-logos/2.12.0/bce/brand/misc/property-image-placeholder.svg'
        );
        const reviewFormUrl =
            travelerStayData.travelerStayReview?.reviewFormUrl || '';
        const invoiceDownloadSummaries =
            travelerStayData.invoiceDownloadSummaries || [];

        // TODO(SE-7924): Move loyalty related logic to a common place.
        // Show correct totals in case of loyalty discount is included.
        const {priceDetails} = conversation;
        const shouldShowLoyaltyTotals =
            priceDetails?.lineItems &&
            priceDetails.lineItems
                .map((lineItem) => lineItem.type)
                .includes('BURN_DISCOUNT') &&
            priceDetails.loyalty?.totalsWithBurn;
        const totals =
            (shouldShowLoyaltyTotals
                ? priceDetails?.loyalty?.totalsWithBurn
                : priceDetails?.totals) || [];

        const conversationId = this.props.conversationId;
        const periodOfStay = getTravelerStayBooking?.periodOfStay || {};
        const {checkInDate, checkInTime, checkOutDate, checkOutTime} =
            periodOfStay;
        const guests = getTravelerStayBooking?.guests || {};
        const {adults, children} = guests;
        const stayData: Stay = createStayObject({
            ctaDetails,
            city,
            conversationId,
            getStayDisplayStatus,
            getTravelerStayBooking,
            gogInvitePopup,
            listing,
            location,
            totals,
            tripSummary,
        });
        const systemDataUtils: System = createSystemDataObject({
            currency: systemData.currency,
            fullLocale: systemData.fullLocale,
            isDesktop: systemData.isDesktop,
            isMobile: systemData.isMobile,
            googleMapApiKey: systemData.googleMapApiKey,
            humanReadableBrandName: systemData.site?.humanReadableBrandName,
            siteName: systemData.site?.name,
            locale: systemData.locale,
            country: systemData.country,
        });

        const title = (
            <Helmet>
                <title>{`${translate(
                    translations,
                    'trav-dash/trav-home',
                    'nav',
                    'th.tabs.inbox'
                )}: ${translate(
                    translations,
                    'trav-dash/trav-home',
                    'messages',
                    `th.inbox.conversation.buttons.${this.state.activeTab}`
                )}`}</title>
            </Helmet>
        );

        const bannerClassName = classNames(
            'image-with-overlay-container Conversation__banner hidden-xs',
            {Conversation__hideBanner: systemData.isDesktop}
        );
        if (this.props.data.loading) {
            return (
                <div className="position-relative" style={{height: '300px'}}>
                    {title}
                    <LoadingOverlay loading />
                </div>
            );
        }

        // Only error if there is no conversation, and no essential stay information
        if (isEmpty(conversation) && isEmpty(travelerStayData)) {
            return (
                <StayProvider value={{...stayData}}>
                    <div className="Conversation conversation-section">
                        {title}
                        <ConversationNavbar
                            activeTab={0}
                            backUrl={backUrl}
                            conversationId={this.props.conversationId}
                            isHybridInbox={isHybridInbox}
                            stayLocation={location}
                            tabs={['back']}
                            translations={translations}
                        />
                        <LoadingError
                            emptyLabel={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.inbox.error.message'
                            )}
                            error={this.props.data.error}
                            systemData={systemData}
                            translations={translations}
                        />
                    </div>
                </StayProvider>
            );
        }

        const canShowArrivalBot =
            this.state.activeTab !== 'messages' && arrivalBot;
        return (
            <SystemProvider value={systemDataUtils}>
                <VaControllerProvider>
                    <MessagesProvider messages={conversation.messages}>
                        <StayProvider
                            value={{
                                ...stayData,
                                backUrl,
                                refetchConversation: this.fetch,
                            }}
                        >
                            <EntryModalProvider
                                path={this.props.path}
                                gogInvitePopup={gogInvitePopup}
                            >
                                <div className="Conversation conversation-section">
                                    {title}
                                    <VaController
                                        getHelpText={() =>
                                            getArrivalBotHelpText(phaseOfStay)
                                        }
                                        onDismissHelpText={() =>
                                            onCloseArrivalBotTooltip(
                                                phaseOfStay
                                            )
                                        }
                                    />
                                    {canShowArrivalBot && (
                                        <Experiment
                                            name={'VRBO_STX_ARRIVAL_SKILL'}
                                            variant={'on'}
                                        >
                                            <VaControllerContext.Consumer>
                                                {(vaContext) => {
                                                    this.updateVaContext(
                                                        checkInDate,
                                                        phaseOfStay,
                                                        vaContext
                                                    );
                                                }}
                                            </VaControllerContext.Consumer>
                                        </Experiment>
                                    )}
                                    <EntryModalContext.Consumer>
                                        {([, , , setGogInvitePopupValue]) => {
                                            if (
                                                gogInvitePopup &&
                                                this.state
                                                    .shouldSetGogInvitePopupValue
                                            ) {
                                                this.setState({
                                                    shouldSetGogInvitePopupValue: false,
                                                });
                                                setGogInvitePopupValue(true);
                                            }
                                        }}
                                    </EntryModalContext.Consumer>
                                    <ConversationNavbar
                                        activeTab={this.state.activeTabIndex}
                                        backUrl={backUrl}
                                        conversationId={
                                            this.props.conversationId
                                        }
                                        isHybridInbox={isHybridInbox}
                                        stayLocation={location}
                                        tabs={this.state.tabs}
                                        translations={translations}
                                    />

                                    <div
                                        className={bannerClassName}
                                        style={{
                                            backgroundImage: `url('${propertyImage}')`,
                                        }}
                                        onClick={
                                            /* istanbul ignore next */ () =>
                                                this.handelBannerClick(
                                                    getTravelerStayBooking.uuid,
                                                    checkOutDate,
                                                    checkInDate
                                                )
                                        }
                                    >
                                        <div className="overlay-header">
                                            <Link
                                                href={listing.detailPageUrl}
                                                target="_blank"
                                                className="overlay-link"
                                            >
                                                <h1 className="text-inverse">
                                                    {location}
                                                </h1>
                                                {listing.listingNumber && (
                                                    <h3 className="text-inverse">
                                                        {translate(
                                                            translations,
                                                            'trav-dash/trav-home',
                                                            'messages',
                                                            'th.inbox.conversation.propertySummary.property.id'
                                                        )}{' '}
                                                        {listing.listingNumber}
                                                    </h3>
                                                )}
                                            </Link>
                                        </div>
                                    </div>

                                    <div className="container container--conversation">
                                        {this.state.activeTab === 'details' && (
                                            <RefundInfoProvider>
                                                <ConversationDetailsDetermineIsMobile
                                                    address={propertyAddress}
                                                    adults={adults}
                                                    arrival={checkInDate}
                                                    backUrl={backUrl}
                                                    children={children}
                                                    contact={contact}
                                                    conversationId={
                                                        this.props
                                                            .conversationId
                                                    }
                                                    countdownSummary={
                                                        countdownSummary
                                                    }
                                                    departure={checkOutDate}
                                                    departureTime={checkOutTime}
                                                    fullPhoneDigits={
                                                        fullPhoneDigits
                                                    }
                                                    fullPhoneNumber={
                                                        fullPhoneNumber
                                                    }
                                                    geoCode={
                                                        listing.geoCode || {}
                                                    }
                                                    globalMessages={
                                                        globalMessages
                                                    }
                                                    gogInvitePopup={
                                                        gogInvitePopup
                                                    }
                                                    isPrimaryGuest={
                                                        getTravelerStayBooking.isPrimaryGuest
                                                    }
                                                    handelBannerClick={
                                                        this.handelBannerClick
                                                    }
                                                    helpUrl={
                                                        this.props.data.helpUrl
                                                    }
                                                    isGuestModalOpen={
                                                        this.props
                                                            .isGuestModalOpen
                                                    }
                                                    isMobile={
                                                        systemData?.isMobile
                                                    }
                                                    listingId={
                                                        listing.listingId
                                                    }
                                                    location={location}
                                                    ownerAvatar={ownerAvatar}
                                                    policies={
                                                        quoteSummary.policies
                                                    }
                                                    priceDetails={
                                                        conversation.priceDetails
                                                    }
                                                    propertyId={
                                                        listing.listingNumber
                                                    }
                                                    propertyImage={
                                                        propertyImage
                                                    }
                                                    propertyImageC5={
                                                        propertyImageC5
                                                    }
                                                    propertyUrl={
                                                        listing.detailPageUrl
                                                    }
                                                    quoteTotals={totals}
                                                    refetch={this.fetch}
                                                    reservationId={
                                                        getTravelerStayBooking.reservationReferenceNumber
                                                    }
                                                    reservationUuid={
                                                        getTravelerStayBooking.uuid
                                                    }
                                                    responseTime={get(
                                                        listing,
                                                        'responsiveness.responseTimeString',
                                                        ''
                                                    )}
                                                    reviewFormUrl={
                                                        reviewFormUrl
                                                    }
                                                    search={search}
                                                    serviceFeeSummary={
                                                        conversation.serviceFeeSummary
                                                    }
                                                    showRefundInfoBtn={
                                                        refundStatusWidget &&
                                                        !isIE(
                                                            systemData.userAgent
                                                        )
                                                    }
                                                    showSendMessageBtn={
                                                        !!conversation
                                                    }
                                                    systemData={systemData}
                                                    translations={translations}
                                                    travelerStayData={
                                                        travelerStayData
                                                    }
                                                    tripSummary={tripSummary}
                                                />
                                            </RefundInfoProvider>
                                        )}

                                        {this.state.activeTab === 'messages' &&
                                            conversation.messages && (
                                                <ConversationMessages
                                                    adults={adults}
                                                    allowsReplies={
                                                        ownerSummary.allowsReplies
                                                    }
                                                    arrival={checkInDate}
                                                    backUrl={backUrl}
                                                    children={children}
                                                    countdownSummary={
                                                        countdownSummary
                                                    }
                                                    contact={contact}
                                                    conversationId={
                                                        this.props
                                                            .conversationId
                                                    }
                                                    departure={checkOutDate}
                                                    expirationDateTime={
                                                        countdownSummary.expirationDateTime
                                                    }
                                                    fullPhoneNumber={
                                                        fullPhoneNumber
                                                    }
                                                    handelBannerClick={
                                                        this.handelBannerClick
                                                    }
                                                    isHybridInbox={
                                                        isHybridInbox
                                                    }
                                                    isPrimaryGuest={
                                                        getTravelerStayBooking.isPrimaryGuest
                                                    }
                                                    location={location}
                                                    onRefreshMessages={
                                                        this.fetch
                                                    }
                                                    ownerAvatar={ownerAvatar}
                                                    priceDetails={
                                                        conversation.priceDetails
                                                    }
                                                    propertyId={
                                                        listing.listingNumber
                                                    }
                                                    propertyImageC5={
                                                        propertyImageC5
                                                    }
                                                    propertyUrl={
                                                        listing.detailPageUrl
                                                    }
                                                    quoteTotals={totals}
                                                    reservationUuid={
                                                        getTravelerStayBooking.uuid
                                                    }
                                                    responseTime={get(
                                                        listing,
                                                        'responsiveness.responseTimeString',
                                                        ''
                                                    )}
                                                    serviceFeeSummary={
                                                        conversation.serviceFeeSummary
                                                    }
                                                    systemData={systemData}
                                                    translations={translations}
                                                    tripSummary={tripSummary}
                                                />
                                            )}

                                        {this.state.activeTab === 'payments' &&
                                            conversation.priceDetails && (
                                                <ConversationPayments
                                                    arrival={checkInDate}
                                                    arrivalTime={checkInTime}
                                                    backUrl={backUrl}
                                                    conversationId={
                                                        this.props
                                                            .conversationId
                                                    }
                                                    countdownSummary={
                                                        countdownSummary
                                                    }
                                                    contact={contact}
                                                    departure={checkOutDate}
                                                    departureTime={checkOutTime}
                                                    expirationDateTime={
                                                        countdownSummary.expirationDateTime
                                                    }
                                                    fullPhoneNumber={
                                                        fullPhoneNumber
                                                    }
                                                    handelBannerClick={
                                                        this.handelBannerClick
                                                    }
                                                    ownerAvatar={ownerAvatar}
                                                    propertyId={
                                                        listing.listingNumber
                                                    }
                                                    propertyImageC5={
                                                        propertyImageC5
                                                    }
                                                    propertyUrl={
                                                        listing.detailPageUrl
                                                    }
                                                    responseTime={get(
                                                        listing,
                                                        'responsiveness.responseTimeString',
                                                        ''
                                                    )}
                                                    invoiceDownloadSummaries={
                                                        invoiceDownloadSummaries
                                                    }
                                                    location={location}
                                                    priceDetails={
                                                        conversation.priceDetails
                                                    }
                                                    reservationUuid={
                                                        getTravelerStayBooking.uuid
                                                    }
                                                    serviceFeeSummary={
                                                        conversation.serviceFeeSummary
                                                    }
                                                    systemData={systemData}
                                                    translations={translations}
                                                    tripSummary={tripSummary}
                                                    vascartUrl={
                                                        vasSummary.vascartUrl
                                                    }
                                                />
                                            )}
                                    </div>
                                </div>
                            </EntryModalProvider>
                        </StayProvider>
                    </MessagesProvider>
                </VaControllerProvider>
            </SystemProvider>
        );
    }
}
export default graphql(ConversationQuery, {
    options: (ownProps) => ({
        variables: {
            conversationId: ownProps.conversationId,
            categories: ownProps.categories,
        },
        errorPolicy: 'all',
    }),
})(Conversation);
